import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import ClientDetailsRow from "layouts/person/ClientDetailsRow";
import { filterClients, sortClients } from "utils/clientLocals";

const paginationOptions = {
  showCount: false,
  alignRight: true,
  compact: true,
  shortPageLabel: true,
  iconNavigation: true,
  grayIconNavigation: true,
  pageSize: 25,
  showPageSizeSelector: false,
  hideNavigationOnSinglePage: true,
};

export default function ClientListTable({ persons, isLoading, makeTable, onEdit }) {
  const [query, setQuery] = useState("");
  const [resetPageKey, setResetPageKey] = useState(0);
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [sort, setSort] = useState(null);
  const filteredPersons = useMemo(
    () => sortClients(filterClients(persons, query), sort),
    [persons, query, sort]
  );
  const { columns, rows } = makeTable({
    persons: filteredPersons,
    onEdit,
    onToggle: (id) => setExpandedPersonId((current) => (current === id ? null : id)),
    expandedPersonId,
  });
  const sortableColumns = columns.map((column) => {
    if (column.accessor !== "Name" && column.accessor !== "City") return column;
    const label = column.Header;
    return {
      ...column,
      Header: (
        <MDBox display="flex" alignItems="center" justifyContent="flex-start" gap={0.25}>
          <span>{label}</span>
          <MDBox display="flex" flexDirection="row">
            {["asc", "desc"].map((direction) => (
              <IconButton
                key={direction}
                size="small"
                aria-label={`Ordenar ${label} ${direction === "asc" ? "ascendente" : "descendente"}`}
                aria-pressed={sort?.column === column.accessor && sort.direction === direction}
                onClick={(event) => {
                  event.stopPropagation();
                  setSort({ column: column.accessor, direction });
                  setResetPageKey((key) => key + 1);
                }}
                sx={{
                  padding: 0,
                  width: 24,
                  height: 22,
                  color:
                    sort?.column === column.accessor && sort.direction === direction
                      ? "info.main"
                      : "text.secondary",
                }}
              >
                {direction === "asc" ? (
                  <ArrowDropUpIcon fontSize="small" />
                ) : (
                  <ArrowDropDownIcon fontSize="small" />
                )}
              </IconButton>
            ))}
          </MDBox>
        </MDBox>
      ),
    };
  });

  return (
    <>
      <MDBox px={3} pb={2} display="flex" justifyContent="flex-end">
        <TextField
          label="Buscar por cliente, local, ciudad, depto., contacto"
          value={query}
          size="small"
          onChange={(event) => {
            setQuery(event.target.value);
            setExpandedPersonId(null);
            setResetPageKey((key) => key + 1);
          }}
          sx={{
            width: { xs: "100%", sm: 520 },
            maxWidth: "100%",
            "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
          }}
        />
      </MDBox>
      <DataTable
        table={{ columns: sortableColumns, rows }}
        isLoading={isLoading}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        resultsPagination
        resultsPaginationOptions={paginationOptions}
        resetPageKey={resetPageKey}
        renderExpandedRow={(row) =>
          row._person?.Id === expandedPersonId ? <ClientDetailsRow person={row._person} /> : null
        }
        noEndBorder
      />
    </>
  );
}

ClientListTable.propTypes = {
  persons: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  makeTable: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
};
